import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { HttpService } from '@app/core/services/http.service';
import { environment } from '@environments/environment';
import { LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { Bunker, InventorySupplyBody, PortReportBody } from '@vessel-reports/port/state/port.types';
import { VesselReport } from '@vessel-reports/state/vessel-reports.types';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PortService {
  public static readonly API: string = `${environment.PROJECT_API}/vessel-report-group/reports`;
  public static readonly VESSEL_API: string = `${environment.PROJECT_API}/vessel_reports`;

  protected readonly httpService: HttpService = inject(HttpService);

  createNewReport(body: PortReportBody): Observable<any> {
    return this.httpService.post<{
      data: any;
    }>(`${PortService.API}`, body);
  }

  getDetailPortReport(report_id: string): Observable<any> {
    return this.httpService.get<{
      data: any;
    }>(`${PortService.API}/${report_id}`, {});
  }

  updateReport(report_id: string, body: any): Observable<any> {
    return this.httpService.put<{
      data: any;
    }>(`${PortService.API}/${report_id}`, body);
  }

  updateDetailReport(body: any): Observable<any> {
    return this.httpService.post<{
      data: any;
    }>(`${PortService.API}/details_bulk`, body);
  }

  submitReport(report_id: string): Observable<any> {
    return this.httpService.put<{
      data: any;
    }>(`${PortService.API}/submit/${report_id}`, {});
  }

  deleteReport(report_id: string): Observable<any> {
    return this.httpService.delete(`${PortService.API}/${report_id}`, {});
  }

  getPreviousReport(vessel_id: string, type?: number): Observable<any> {
    const url = type ? `${PortService.VESSEL_API}/previous_report?filter[report_type_id]=${type}` : `${PortService.VESSEL_API}/previous_report`;
    return this.httpService.get<{
      data: VesselReport;
    }>(url, {});
  }

  getInventoriesSupply(report_id: string): Observable<any> {
    return this.httpService.get<{
      data: VesselReport;
    }>(`${PortService.VESSEL_API}/report_supplies/${report_id}`, {});
  }

  createInventorySupply(body: InventorySupplyBody): Observable<any> {
    return this.httpService.post<{
      data: any;
    }>(`${PortService.VESSEL_API}/report_supplies`, body);
  }

  updateInventorySupply(id: string, body: InventorySupplyBody): Observable<any> {
    return this.httpService.put<{
      data: any;
    }>(`${PortService.VESSEL_API}/report_supplies/${id}`, body);
  }

  deleteInventorySupply(id: string) {
    return this.httpService.delete(`${PortService.VESSEL_API}/report_supplies/${id}`, {});
  }

  createReportBunker(body: any): Observable<any> {
    return this.httpService.post<{
      data: any;
    }>(`${PortService.API}/bunkers`, body);
  }

  updateReportBunker(report_id: string, body: any): Observable<any> {
    return this.httpService.put<{
      data: any;
    }>(`${PortService.API}/bunkers/${report_id}`, body);
  }

  deleteReportBunker(report_id: string): Observable<any> {
    return this.httpService.delete(`${PortService.API}/bunkers/${report_id}`, {});
  }

  getBunker(bunker_id: string): Observable<any> {
    return this.httpService.get<{
      data: Bunker;
    }>(`${PortService.API}/bunkers/${bunker_id}`, {});
  }

  getAdvanceFilters(key: string, limit?: number, queryParams?: Params): Observable<{ data: any[] }> {
    return this.httpService.get<{ data: any[] }>(`${PortService.API}/advance_filters`, {
      params: {
        advance_filter: key,
        limit: limit ?? LIMIT_PER_PAGE_DEFAULT,
        ...queryParams
      }
    });
  }
}
